import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SEARCH_FIELD_PROPS } from '@/components/shared/touchInput';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HighlightedSnippet } from '@/components/ui/highlighted-snippet';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAddStudiesToFolder } from '@/hooks/use-study-folders';
import { toast } from '@/hooks/use-toast';
import { getStudyList } from '@/lib/api';
import { NUMERIC_DATE, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Calendar, FileText, GripVertical, Plus, Search, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
const REPORT_SECTION_OPTIONS = [
    { value: 'all', labelKey: 'studySearch.sectionAll' },
    { value: 'impression', labelKey: 'studySearch.sectionImpression' },
    { value: 'findings', labelKey: 'studySearch.sectionFindings' },
];
const SEARCH_FIELDS = [
    {
        value: 'patientName',
        labelKey: 'studySearch.patientName',
        placeholderKey: 'studySearch.searchByPatient',
        inputType: 'text',
    },
    {
        value: 'ownerName',
        labelKey: 'studySearch.ownerName',
        placeholderKey: 'studySearch.searchByOwner',
        inputType: 'text',
    },
    {
        value: 'description',
        labelKey: 'studySearch.description',
        placeholderKey: 'studySearch.searchByDescription',
        inputType: 'text',
    },
    {
        value: 'modality',
        labelKey: 'studySearch.modality',
        placeholderKey: 'studySearch.searchByModality',
        inputType: 'text',
    },
    {
        value: 'accessionNumber',
        labelKey: 'studySearch.accessionNumber',
        placeholderKey: 'studySearch.searchByAccession',
        inputType: 'text',
    },
    {
        value: 'studyDate',
        labelKey: 'studySearch.studyDate',
        placeholderKey: 'studySearch.selectDate',
        inputType: 'date',
    },
    {
        value: 'reportKeyword',
        labelKey: 'studySearch.reportKeyword',
        placeholderKey: 'studySearch.searchReportContent',
        inputType: 'text',
    },
];
// MIME type for study drag operations (matches WorklistTable)
const STUDY_MIME_TYPE = 'application/x-study-id';
function StudySearchResult({ study, onAddToFolder, showAddButton, onNavigate, }) {
    const { t } = useTranslation();
    const [isDragging, setIsDragging] = useState(false);
    const handleDragStart = (e) => {
        setIsDragging(true);
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData(STUDY_MIME_TYPE, study.id);
        e.dataTransfer.setData('text/plain', study.id);
    };
    const handleDragEnd = () => {
        setIsDragging(false);
    };
    const patientName = study.patient.name || t('studySearch.unknownPatient');
    const description = study.study.description || t('studySearch.noDescription');
    const dateStr = formatDate(study.study.dateTime, NUMERIC_DATE) || t('studySearch.unknownDate');
    return (_jsxs("div", { className: cn('hover:bg-muted/50 flex cursor-grab items-center gap-2 rounded-md border border-transparent p-2 transition-colors', isDragging && 'border-primary border-dashed opacity-50'), draggable: true, onDragStart: handleDragStart, onDragEnd: handleDragEnd, "data-testid": `search-result-${study.id}`, children: [_jsx(GripVertical, { className: "text-muted-foreground h-4 w-4 shrink-0" }), _jsxs("div", { className: cn('min-w-0 flex-1', onNavigate && 'cursor-pointer'), onClick: onNavigate ? () => onNavigate(study.id) : undefined, title: onNavigate ? t('studySearch.openReport') : undefined, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(User, { className: "text-muted-foreground h-3.5 w-3.5 shrink-0" }), _jsx("span", { className: "truncate text-sm font-medium", children: patientName })] }), _jsxs("div", { className: "mt-0.5 flex items-center gap-2", children: [_jsx(FileText, { className: "text-muted-foreground h-3.5 w-3.5 shrink-0" }), _jsx("span", { className: "text-muted-foreground truncate text-xs", children: description })] }), _jsxs("div", { className: "mt-0.5 flex items-center gap-2", children: [_jsx(Calendar, { className: "text-muted-foreground h-3.5 w-3.5 shrink-0" }), _jsx("span", { className: "text-muted-foreground text-xs", children: dateStr }), study.study.modality && (_jsx("span", { className: "bg-muted rounded px-1.5 py-0.5 text-xs", children: study.study.modality }))] }), study.reportSnippet && (_jsx("div", { className: "mt-1", children: _jsx(HighlightedSnippet, { snippet: study.reportSnippet }) }))] }), showAddButton && onAddToFolder && (_jsx(Button, { variant: "ghost", size: "icon", className: "h-8 w-8 shrink-0", onClick: () => onAddToFolder(study.id), title: t('folder.addToFolder'), children: _jsx(Plus, { className: "h-4 w-4" }) }))] }));
}
export function StudySearchDialog({ open, onOpenChange, targetFolder }) {
    const { t } = useTranslation();
    const [searchField, setSearchField] = useState('patientName');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [reportSection, setReportSection] = useState('all');
    const addStudiesToFolder = useAddStudiesToFolder();
    const [, setLocation] = useLocation();
    const currentFieldConfig = SEARCH_FIELDS.find((f) => f.value === searchField);
    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);
    // Reset search when dialog closes
    /* eslint-disable react-hooks/set-state-in-effect -- Intentional: reset dialog state on close */
    useEffect(() => {
        if (!open) {
            setSearchQuery('');
            setDebouncedQuery('');
            setSearchField('patientName');
            setReportSection('all');
        }
    }, [open]);
    /* eslint-enable react-hooks/set-state-in-effect */
    // Build query params based on selected field
    const buildQueryParams = useCallback(() => {
        const baseParams = {
            pageSize: 20,
            sortBy: 'StudyDate',
            sortDirection: 'Desc',
        };
        if (!debouncedQuery)
            return baseParams;
        switch (searchField) {
            case 'patientName':
                return { ...baseParams, patientName: debouncedQuery };
            case 'ownerName':
                return { ...baseParams, ownerName: debouncedQuery };
            case 'description':
                return { ...baseParams, description: debouncedQuery };
            case 'modality':
                return { ...baseParams, modality: debouncedQuery.toUpperCase() };
            case 'accessionNumber':
                return { ...baseParams, accessionNumber: debouncedQuery };
            case 'reportKeyword':
                return { ...baseParams, reportKeyword: debouncedQuery, reportSection };
            case 'studyDate': {
                // For date search, search studies from that date
                const date = new Date(debouncedQuery);
                if (isNaN(date.getTime()))
                    return baseParams;
                const startOfDay = new Date(date);
                startOfDay.setHours(0, 0, 0, 0);
                const endOfDay = new Date(date);
                endOfDay.setHours(23, 59, 59, 999);
                return {
                    ...baseParams,
                    fromDateTime: startOfDay.toISOString(),
                    toDateTime: endOfDay.toISOString(),
                };
            }
            default:
                return baseParams;
        }
    }, [searchField, debouncedQuery, reportSection]);
    // Determine minimum query length based on field
    const minQueryLength = searchField === 'studyDate' ? 10 : searchField === 'modality' ? 1 : 2;
    // Check if current field is coming soon (not yet implemented)
    const isComingSoon = currentFieldConfig.comingSoon === true;
    // Search query
    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['studySearch', searchField, debouncedQuery, reportSection],
        queryFn: () => getStudyList(buildQueryParams()),
        enabled: debouncedQuery.length >= minQueryLength && !isComingSoon,
        staleTime: 30000,
    });
    const handleAddToFolder = useCallback((studyId) => {
        if (!targetFolder) {
            toast({
                title: t('studySearch.noFolderSelected'),
                description: t('studySearch.noFolderHint'),
                variant: 'destructive',
            });
            return;
        }
        addStudiesToFolder.mutate({ folderId: targetFolder.id, request: { studyIds: [studyId] } }, {
            onSuccess: () => {
                toast({
                    title: t('studySearch.studyAdded'),
                    description: t('studySearch.studyAddedTo', { name: targetFolder.name }),
                });
            },
            onError: (error) => {
                toast({
                    title: t('studySearch.failedToAdd'),
                    description: error instanceof Error ? error.message : t('studySearch.failedToAddDefault'),
                    variant: 'destructive',
                });
            },
        });
    }, [targetFolder, addStudiesToFolder, t]);
    const studies = data?.results ?? [];
    const hasQuery = debouncedQuery.length >= minQueryLength;
    const showLoading = isLoading || isFetching;
    // Handle field change - clear search when switching fields
    const handleFieldChange = (value) => {
        setSearchField(value);
        setSearchQuery('');
        setDebouncedQuery('');
    };
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { className: "max-w-lg", children: [_jsx(DialogHeader, { children: _jsx(DialogTitle, { children: t('studySearch.title') }) }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex gap-2", children: [_jsxs(Select, { value: searchField, onValueChange: (v) => handleFieldChange(v), children: [_jsx(SelectTrigger, { className: "w-[140px] shrink-0", "data-testid": "search-field-select", children: _jsx(SelectValue, {}) }), _jsx(SelectContent, { children: SEARCH_FIELDS.map((field) => (_jsx(SelectItem, { value: field.value, children: t(field.labelKey) }, field.value))) })] }), _jsxs("div", { className: "relative flex-1", children: [_jsx(Search, { className: "text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" }), _jsx(Input, { type: currentFieldConfig.inputType, ...SEARCH_FIELD_PROPS, value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), placeholder: isComingSoon ? t('studySearch.comingSoon') : t(currentFieldConfig.placeholderKey), className: "pl-9", disabled: isComingSoon, "data-testid": "study-search-input" })] })] }), searchField === 'reportKeyword' && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-muted-foreground text-xs", children: t('studySearch.sectionLabel') }), _jsxs(Select, { value: reportSection, onValueChange: (v) => setReportSection(v), children: [_jsx(SelectTrigger, { className: "h-8 w-[160px]", "data-testid": "report-section-select", children: _jsx(SelectValue, {}) }), _jsx(SelectContent, { children: REPORT_SECTION_OPTIONS.map((opt) => (_jsx(SelectItem, { value: opt.value, children: t(opt.labelKey) }, opt.value))) })] })] })), targetFolder ? (_jsx("p", { className: "text-muted-foreground text-xs", children: t('studySearch.helpWithFolder', { name: targetFolder.name }) })) : (_jsx("p", { className: "text-muted-foreground text-xs", children: t('studySearch.helpNoFolder') })), _jsxs(ScrollArea, { className: "h-[300px] pr-3", children: [isComingSoon && (_jsxs("div", { className: "flex h-full flex-col items-center justify-center text-center", children: [_jsx(FileText, { className: "text-muted-foreground/30 mb-2 h-10 w-10" }), _jsx("p", { className: "text-muted-foreground text-sm font-medium", children: t('studySearch.comingSoonTitle') }), _jsx("p", { className: "text-muted-foreground mt-1 text-xs", children: t('studySearch.comingSoonDescription') })] })), !isComingSoon && !hasQuery && (_jsxs("div", { className: "flex h-full flex-col items-center justify-center text-center", children: [_jsx(Search, { className: "text-muted-foreground/30 mb-2 h-10 w-10" }), _jsx("p", { className: "text-muted-foreground text-sm", children: searchField === 'studyDate'
                                                ? t('studySearch.selectDateHint')
                                                : searchField === 'modality'
                                                    ? t('studySearch.enterModality')
                                                    : t('studySearch.minChars', { count: minQueryLength }) })] })), !isComingSoon && hasQuery && showLoading && (_jsx("div", { className: "space-y-2", children: Array.from({ length: 5 }).map((_, i) => (_jsx(Skeleton, { className: "h-16 w-full" }, `skeleton-${i}`))) })), !isComingSoon && hasQuery && !showLoading && studies.length === 0 && (_jsxs("div", { className: "flex h-full flex-col items-center justify-center text-center", children: [_jsx(FileText, { className: "text-muted-foreground/30 mb-2 h-10 w-10" }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('studySearch.noResults', { query: debouncedQuery }) })] })), !isComingSoon && hasQuery && !showLoading && studies.length > 0 && (_jsxs("div", { className: "space-y-1", children: [studies.map((study) => (_jsx(StudySearchResult, { study: study, onAddToFolder: handleAddToFolder, showAddButton: !!targetFolder, onNavigate: searchField === 'reportKeyword'
                                                ? (studyId) => {
                                                    onOpenChange(false);
                                                    setLocation(`/report/${encodeURIComponent(studyId)}`);
                                                }
                                                : undefined }, study.id))), data && data.total > studies.length && (_jsx("p", { className: "text-muted-foreground py-2 text-center text-xs", children: t('studySearch.showingResults', { count: studies.length, total: data.total }) }))] }))] })] })] }) }));
}
